import {
  FlowDownFieldVendorDto,
  PrivacyCenterDto,
} from "../../../../../common/service/server/dto/PrivacyCenterDto";

export type FlowDownField = FlowDownFieldVendorDto;
export type FlowDownFieldStatus = { saving?: boolean; optimisticValue?: boolean; error?: string };
export const flowDownFieldId = (field: FlowDownField) => `${field.source}:${field.key}`;

export function groupFlowDownFields(fields: FlowDownField[]) {
  return (["OPT_OUT_FORM", "REQUEST_FORM", "LIMIT"] as const).map((source) => ({
    id: source,
    label: source === "REQUEST_FORM" ? "Request Form" : "Opt-Out Form",
    fields: fields
      .filter((field) => field.source === source)
      .map((field) => ({ id: flowDownFieldId(field), label: field.label })),
  }));
}

/** Patch only the acknowledged permission; preserve other concurrent saves and collection edits. */
export function updateFlowDownPermission(
  centers: PrivacyCenterDto[] | undefined,
  field: FlowDownField,
  enabled: boolean,
) {
  return centers?.map((center) => ({
    ...center,
    flowDownFields: center.flowDownFields?.map((current) =>
      flowDownFieldId(current) === flowDownFieldId(field)
        ? { ...current, includeInVendorRequests: enabled }
        : current,
    ),
  }));
}

/** One in-flight write per row; independent rows may save concurrently. */
export function createFlowDownAutosave({
  persist,
  cancelReads,
  commit,
  refresh,
  onStatus,
}: {
  persist: (field: FlowDownField, enabled: boolean) => Promise<void>;
  cancelReads: () => Promise<void>;
  commit: (field: FlowDownField, enabled: boolean) => void;
  refresh: () => Promise<unknown>;
  onStatus: (id: string, status?: FlowDownFieldStatus) => void;
}) {
  const pending = new Set<string>();
  return async (field: FlowDownField, enabled: boolean) => {
    const id = flowDownFieldId(field);
    if (pending.has(id)) return;
    pending.add(id);
    onStatus(id, { saving: true, optimisticValue: enabled });
    try {
      await cancelReads();
      await persist(field, enabled);
      // Discard any GET started before this write committed, then patch only this field.
      await cancelReads();
      commit(field, enabled);
      onStatus(id);
    } catch {
      // No optimistic cache write to roll back: the last confirmed value remains intact.
      onStatus(id, { error: "Couldn’t save. Toggle again to retry." });
    } finally {
      pending.delete(id);
      if (!pending.size) {
        // The query hook reports refresh failures. They do not turn an acknowledged PUT into a failed save.
        void refresh().catch(() => undefined);
      }
    }
  };
}
